/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import type { AxiosRequestConfig } from "axios";
import { apiInstance2, apiInstance3, RETRIED } from "@app/utils/api";

// Fetch the form definition (v2 shape: { id, title, questions[], ... }).
export const getFormDataById = async (formId: string) => {
  const res = await apiInstance3.get(`/webhook/bi-forms/${formId}`);
  return res.data;
};

// Learner submission — { responses: [{ questionId, answer }], componentId? }.
export const submitLearnerResponse = async (formId: string, payload: any) => {
  const res = await apiInstance3.post(`/learner/forms/${formId}/submit`, payload);
  return res.data;
};

// Fetch a learner's draft / existing submission. Returns the submission object directly under `data`
// ({ id, formId, status, responses: [{ questionId, answer }], ... }).
export const getLearnerFeedbackResponse = async (params: any) => {
  const res = await apiInstance3.get("/learner/forms/session-feedback-response", { params });
  return res.data;
};

// Fetch a learner's submission for.
export const getLearnerResponseByFormId = async (formId: string, timeoutMs?: number) => {
  const res = await apiInstance3.get(`/learner/forms/${formId}`, timeoutMs ? { timeout: timeoutMs } : undefined);
  return res.data;
};

// Fetch a user's submission for the admin view.
export const fetchLearnerResponsesForAdminForSessionFeedbackForm = async (params: any) => {
  const res = await apiInstance3.get("/tenantAdmin/forms/session-feedback-response", { params });
  return res.data;
};

// Save a learner's draft i.e coach give feedback to student and save it as draft.
export const saveDraftResponse = async (formId: string, payload: any) => {
  const res = await apiInstance3.post(`/learner/forms/${formId}/draft`, payload);
  return res.data;
};

// Feedback response submitted by admin.
export const submitAdminResponse = async (formId: string, payload: any) => {
  const res = await apiInstance3.post(`/tenantAdmin/forms/${formId}/submit`, payload);
  return res.data;
};

// Fetch a learner's submission for the admin view for forms other than session feedback form.
export const fetchLearnerResponsesForAdmin = async (formId: string, submissionId: string) => {
  const res = await apiInstance3.get(`/tenantAdmin/forms/${formId}/submissions/${submissionId}`);
  return res.data;
};

// ----- Geo capture: coordinates -> a place name -----
// Reverse geocodes a capture's coordinates to a locality string ("Madhyamgram, Kolkata"), server-side so the
// Google key never reaches the browser.
//
// There is no error branch to write here beyond the network itself: core-api answers 200 `{ place: null }`
// for a missing key, a Google failure, a timeout, or no locality at those coordinates. `null` means "show the
// coordinates instead", never "something went wrong".
//
// Display only. The place kept on the submission is resolved by core-api from the submitted coordinates; a
// place name sent from here would be forgeable on an attendance record, and is discarded.
export const reverseGeocode = async (latitude: number, longitude: number): Promise<string | null> => {
  // Shorter than the instance's 30 s: this decorates a line that already reads correctly without it. 8 s is per
  // attempt; on a tenant host the shared retry interceptor tries once more on the baked origin, so the worst case
  // is about 16 s. Nothing waits on it either way, and the coordinates stay on screen meanwhile.
  const res = await apiInstance3.get("/learner/forms/geo/reverse-geocode", { params: { lat: latitude, lng: longitude }, timeout: 8000 });
  return res.data?.data?.place ?? null;
};

// ----- Geo capture: office distance check -----
// What core-api tells the capture form about the office check for one request. `office` is null when the student's
// office has no coordinates. Nothing here is ever shown as numbers: only the status banner reads it.
export interface GeoCaptureContext {
  distanceCheck: boolean;
  mode: string | null;
  needsWorkLocation: boolean;
  office: { latitude: number; longitude: number } | null;
  radiusMeters: number | null;
  /** The request's window (ISO). Absent on an older server: the form then shows no banner and does not lock. */
  opensAt?: string;
  /** null = never closes. */
  closesAt?: string | null;
  /** The server's clock when it answered, used to correct for a wrong device clock. */
  serverNow?: string;
}

export const getGeoCaptureContext = async (relatedModelId: string): Promise<GeoCaptureContext> => {
  const res = await apiInstance3.get("/learner/forms/geo-capture/context", { params: { relatedModelId } });
  return res.data?.data ?? res.data;
};

// ----- Week 0: live check of the office location -----
export interface OfficeLocationCheck {
  valid: boolean;
  found: boolean;
  mapUrl: string | null;
}

const OFFICE_CHECK_TIMEOUT_MS = 8000;

// Short timeout, and no retry on the baked origin (marking the request as already retried): a slow or failing
// check is reported as "error" at once (Submit stays allowed) instead of costing a second 8 s on another host.
export const checkOfficeLocation = async (value: string): Promise<OfficeLocationCheck> => {
  const res = await apiInstance3.post("/learner/forms/office-location/check", { value }, {
    timeout: OFFICE_CHECK_TIMEOUT_MS,
    [RETRIED]: true
  } as AxiosRequestConfig);
  const data = res.data?.data ?? res.data;
  return { valid: Boolean(data?.valid), found: Boolean(data?.found), mapUrl: data?.mapUrl ?? null };
};

// ----- Job forms: Company & Job Title option sources -----
// Public master-list search on core-api v2 (same endpoint bi-main's Experience form uses).
// Response: { data: { items: [{ Id, Name, ... }], pagination } }.
export interface MasterListItem {
  Id: string;
  Name: string;
}

const searchMasterList = async (slug: "company" | "role", searchQuery: string): Promise<{ items: MasterListItem[] }> => {
  const params: Record<string, unknown> = { pageNumber: 1, perPage: 100 };
  if (searchQuery && searchQuery.trim()) params.searchQuery = searchQuery.trim();
  const res = await apiInstance2.get(`/master-list/search/${slug}`, { params });
  return res.data?.data || { items: [] };
};

export const searchCompanies = (searchQuery: string) => searchMasterList("company", searchQuery);
export const searchRoles = (searchQuery: string) => searchMasterList("role", searchQuery);

// ----- Off-campus request: Company, Role and Location -----
// Deliberately separate from `searchMasterList` above, which stays exactly as it is: the job form's
// Company / Job Title keep searching `/master-list/search/:slug`. Company here is the learner's own
// tenant list, searched server-side through the shared `useMasterListSearch` hook and its 500 ms
// debounce; Role and Location are the Airtable master list's `role` and `city` slugs, fetched once
// together and filtered in the browser. Every source is normalised to `MasterListItem`.

/** One tenant company as `GET /v3/learner/companies` returns it. The option's id is its Mongo `_id`. */
interface LearnerCompany {
  _id: string;
  companyName: string;
}

/** core-api rejects a longer `name` on the learner company list (Joi `max(100)`). */
const LEARNER_SEARCH_MAX_LENGTH = 100;

/** core-api's default page size for the learner company list. */
const LEARNER_LIST_PAGE_SIZE = 100;

/**
 * Companies for the off-campus request form: the signed-in learner's tenant list (the tenant's own
 * companies plus the global ones it has not forked), searched server-side and sorted by name. Needs
 * the learner token this app already has; the tenant is read from the token, never sent from here.
 *
 * core-api notes the company query has no declared index, which is why every call lands here through
 * the shared search hook's debounce rather than per keystroke.
 */
export const searchLearnerCompanies = async (searchQuery: string): Promise<{ items: MasterListItem[] }> => {
  const params: Record<string, unknown> = { limit: LEARNER_LIST_PAGE_SIZE };
  const search = (searchQuery || "").trim();
  // Sent as `name`: core-api JSON-parses any v3 `search` query, which turned the typed text into a 400.
  if (search) params.name = search.slice(0, LEARNER_SEARCH_MAX_LENGTH);
  const res = await apiInstance3.get("/learner/companies", { params });
  const companies: LearnerCompany[] = res.data?.data?.companies || [];
  return { items: companies.map(({ _id, companyName }) => ({ Id: String(_id), Name: companyName })) };
};

/**
 * The Airtable master-list slugs the off-campus request form reads: Role and Location.
 *
 * These are the same two slugs the job form posts a job against, so a role id picked here is the id a
 * job stores. There is no backend of our own behind them: `role` and `city` are whatever the master
 * list holds. `city` carries ids too, but Location is an open field and submits a place name alone,
 * so its ids are dropped by the layout.
 */
export const MASTER_LIST_SLUGS = ["role", "city"] as const;

export type MasterListSlug = (typeof MASTER_LIST_SLUGS)[number];

/** The options of every requested slug, keyed by slug. A slug the response omits comes back empty. */
export type MasterListsBySlug = Record<MasterListSlug, MasterListItem[]>;

/** One slug's group in the response: its own slug, and the options under it. */
interface MasterListSlugGroup {
  Slug?: unknown;
  Options?: unknown;
}

/**
 * The options of one group, kept to the entries that have both an id and a name and deduped by id.
 *
 * Read defensively on purpose: core-api proxies this body verbatim from an external Airtable service,
 * so nothing here is validated by a schema of ours. A duplicate id would also make `MasterListField`
 * resolve a pick to whichever copy came first.
 */
const readSlugOptions = (group: MasterListSlugGroup): MasterListItem[] => {
  const options = Array.isArray(group.Options) ? group.Options : [];
  const seen = new Set<string>();
  const items: MasterListItem[] = [];
  options.forEach((option: unknown) => {
    const { Id, Name } = (option || {}) as { Id?: unknown; Name?: unknown };
    const id = Id === null || Id === undefined ? "" : String(Id);
    const name = typeof Name === "string" ? Name.trim() : "";
    if (!id || !name || seen.has(id)) return;
    seen.add(id);
    items.push({ Id: id, Name: name });
  });
  return items;
};

/**
 * Role and Location for the off-campus request form, in one call to the Airtable master list.
 *
 * `GET /v2/integrations/air-table/getAllBySlugs?Slug=["role","city"]`, the same route and the same
 * slugs the job form reads, behind `unverifiedUserAuth` so the learner token this app already has
 * reaches it. Both lists are asked for together and held for the form's lifetime: they are static for
 * a session, so Role filters in the browser and never searches server-side.
 *
 * The body is `{ data: [{ Slug, Options: [{ Id, Name }] }, …] }`, one group per requested slug — the
 * shape tenant-admin reads today. Groups are matched by their own `Slug` rather than by position, and
 * the `{ data: { data } }` nesting some of core-api's proxies add is accepted too.
 *
 * Rejects when the response carries no usable `role` options at all, which is the same thing to a
 * learner as the call failing: Role is a closed field, so without that list the form cannot be
 * submitted and the layout has to say so rather than show an empty dropdown. A missing or empty
 * `city` is not a failure — Location still takes what the learner types. `requireRoles: false` (an
 * off-campus form with a Location picker but no Role picker) skips the role check.
 */
export const fetchMasterLists = async ({ requireRoles = true }: { requireRoles?: boolean } = {}): Promise<MasterListsBySlug> => {
  const res = await apiInstance2.get("/integrations/air-table/getAllBySlugs", { params: { Slug: JSON.stringify(MASTER_LIST_SLUGS) } });
  const body = res.data?.data;
  const groups: MasterListSlugGroup[] = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
  const lists = { role: [], city: [] } as MasterListsBySlug;
  MASTER_LIST_SLUGS.forEach(slug => {
    const group = groups.find(entry => typeof entry?.Slug === "string" && entry.Slug.trim().toLowerCase() === slug);
    lists[slug] = group ? readSlugOptions(group) : [];
  });
  // A form with no Role picker needs only `city`: a missing role list must not fail the read.
  if (requireRoles && lists.role.length === 0) throw new Error("The master list returned no roles");
  return lists;
};

// ----- Tenant ACL (nav tabs + company branding) -----
// Used by the job form to show the tenant logo and resolve the "home" destination (first nav tab).
export interface TenantAclTab {
  link: string;
  name: string;
  image?: string;
  order?: number;
}

export interface TenantAclResponse {
  tenantAcl?: { aclCMS?: { tabs?: TenantAclTab[] } };
  companyInfo?: { brandName?: string; logo?: string; logos?: { thumbnail?: string; small?: string; medium?: string; large?: string } };
}

export const getTenantAcl = async (): Promise<TenantAclResponse> => {
  const res = await apiInstance3.get("/integrations/tenant/acl");
  return res.data?.data || {};
};

// ----- S3 multipart upload (job-form file / video answers) -----
// Backed by core-api common endpoints (userAuth-gated): /common/s3/multipart/*.
export const s3MultipartCreate = async (payload: { key: string; contentType: string }) =>
  (await apiInstance3.post("/common/s3/multipart/create", payload)).data;

export const s3MultipartSignPart = async (payload: { key: string; uploadId: string; partNumber: number }) =>
  (await apiInstance3.post("/common/s3/multipart/sign-part", payload)).data;

export const s3MultipartComplete = async (payload: { key: string; uploadId: string; parts: { ETag: string; PartNumber: number }[] }) =>
  (await apiInstance3.post("/common/s3/multipart/complete", payload)).data;

export const s3MultipartAbort = async (payload: { key: string; uploadId: string }) => (await apiInstance3.post("/common/s3/multipart/abort", payload)).data;

// ----- Tabular (spreadsheet-style) session feedback -----

// Learners of a session (one grid row each) — LEARNER participants only, coaches excluded server-side.
export const getSessionLearners = async (sessionId: string) => {
  const res = await apiInstance3.get(`/learner/forms/sessions/${sessionId}/learners`);
  return res.data;
};

// The caller's existing feedback for a session, keyed by learner (givenToUserId) — powers grid prefill.
export const getSessionResponses = async (formId: string, sessionId: string) => {
  const res = await apiInstance3.get(`/learner/forms/${formId}/session-responses`, { params: { sessionId } });
  return res.data;
};

// Submit the whole grid at once — payload { sessionId, responses: [{ givenToUserId, answers: [{ questionId, answer }] }] }.
export const bulkSubmitResponses = async (formId: string, payload: any) => {
  const res = await apiInstance3.post(`/learner/forms/${formId}/bulk-submit`, payload);
  return res.data;
};

// Save the whole grid as drafts — same payload shape as bulkSubmitResponses.
export const bulkDraftResponses = async (formId: string, payload: any) => {
  const res = await apiInstance3.post(`/learner/forms/${formId}/bulk-draft`, payload);
  return res.data;
};
